import React from 'react';
import { Card, Flex, Skeleton, Grid, Tabs, Progress } from 'antd';
import { useTranslation } from 'react-i18next';
import { NS } from '../../i18n';
// The page's own sheet: this skeleton is the route's Suspense fallback and
// RouteSkeleton's (initial bundle), so it paints before AccountSettings' chunk.
// Safe to load eagerly — every rule in it is scoped to an `account-settings-*` class.
import './AccountSettings.css';

const { useBreakpoint } = Grid;

interface AccountSettingsSkeletonProps {
  isMobile?: boolean;
}

/** A label + value pair, as the billing and payment cards stack them. */
const LabelValue: React.FC<{ labelWidth: number; valueWidth: number }> = ({
  labelWidth,
  valueWidth,
}) => (
  <Flex vertical gap={8}>
    <Skeleton.Input active size="small" style={{ width: labelWidth, minWidth: 0, height: 14 }} />
    <Skeleton.Input active size="small" style={{ width: valueWidth, minWidth: 0, height: 16 }} />
  </Flex>
);

/**
 * The Billing screen (/settings/account) while it loads, in the page's own
 * frame: breadcrumb, the card with the title and the tabs (real, translated
 * labels), then the Usage & Payment tab — the one the page opens on. Every
 * box mirrors the loaded one (32px agent icon, the "Next billing" pill, the
 * 150px fee cards, the 315px payment cards) so nothing jumps when data lands.
 */
export const AccountSettingsSkeleton: React.FC<AccountSettingsSkeletonProps> = ({
  isMobile = false,
}) => {
  const { t } = useTranslation(NS);
  const screens = useBreakpoint();
  const isSmallMobile = !screens.sm;
  const actualIsMobile = isMobile || !screens.md;

  const feeCardStyle: React.CSSProperties = {
    flex: actualIsMobile ? '1 1 0' : '0 0 150px',
    minWidth: actualIsMobile ? '120px' : '150px',
  };

  const usagePaymentSkeleton = (
    <Flex vertical gap={24} style={{ width: '100%' }}>
      <Card className="account-settings-usage-agents-card" style={{ width: '100%' }}>
        <Skeleton.Input
          active
          style={{ width: 140, minWidth: 0, height: 24, marginBottom: 'var(--silia-space-md)' }}
        />
        <Flex vertical gap={16}>
          {[0, 1].map((index) => (
            <Card key={index} className="account-settings-agent-card">
              <Flex vertical gap={16}>
                <Flex
                  gap={12}
                  align={actualIsMobile ? 'flex-start' : 'center'}
                  wrap="wrap"
                  style={{ width: '100%' }}
                >
                  <Skeleton.Avatar
                    active
                    shape="square"
                    style={{
                      width: 'var(--silia-ctl-height-sm)',
                      height: 'var(--silia-ctl-height-sm)',
                      borderRadius: 'var(--silia-radius-sm)',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: actualIsMobile ? '1 1 100%' : '1 0 0', minWidth: 0 }}>
                    <Skeleton.Input active style={{ width: 180, minWidth: 0, height: 24 }} />
                  </div>
                  {/* The "Next billing" pill. */}
                  <Flex
                    gap={8}
                    align="center"
                    style={{
                      background: 'var(--silia-surface-tint-1)',
                      border: '1px solid var(--silia-fill-2)',
                      borderRadius: '8px',
                      padding: '4px 12px',
                      flexShrink: 0,
                    }}
                  >
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: 90, minWidth: 0, height: 14 }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: 90, minWidth: 0, height: 16 }}
                    />
                  </Flex>
                </Flex>

                <Flex gap={16} wrap="wrap" style={{ width: '100%' }}>
                  <Card
                    className="account-settings-billing-card"
                    style={{
                      flex: actualIsMobile ? '1 1 100%' : '1 0 0',
                      minWidth: actualIsMobile ? '100%' : '200px',
                      width: actualIsMobile ? '100%' : 'auto',
                    }}
                  >
                    <Flex vertical gap={8}>
                      <Skeleton.Input
                        active
                        size="small"
                        style={{ width: 120, minWidth: 0, height: 14 }}
                      />
                      <Flex gap={24} align="center" wrap="wrap">
                        <Progress
                          percent={0}
                          showInfo={false}
                          style={{ flex: 1, minWidth: '150px' }}
                        />
                        <Skeleton.Input
                          active
                          size="small"
                          style={{ width: 150, minWidth: 0, height: 14 }}
                        />
                      </Flex>
                    </Flex>
                  </Card>
                  {[100, 80, 60, 50].map((labelWidth) => (
                    <Card
                      key={labelWidth}
                      className="account-settings-billing-card"
                      style={feeCardStyle}
                    >
                      <LabelValue labelWidth={labelWidth} valueWidth={90} />
                    </Card>
                  ))}
                </Flex>
              </Flex>
            </Card>
          ))}
        </Flex>
      </Card>

      <Card className="account-settings-payment-methods-card" style={{ width: '100%' }}>
        <Flex vertical gap={16}>
          <Skeleton.Input active style={{ width: 160, minWidth: 0, height: 24 }} />
          <Flex gap={16} wrap="wrap">
            <Card
              className="account-settings-payment-card"
              style={{
                flex: actualIsMobile ? '1 1 100%' : '0 0 315px',
                width: actualIsMobile ? '100%' : '315px',
              }}
            >
              {/* The accent disc with the card glyph. */}
              <Skeleton.Avatar
                active
                shape="square"
                style={{
                  width: 'var(--silia-ctl-height-sm)',
                  height: 'var(--silia-ctl-height-sm)',
                  borderRadius: 'var(--silia-radius-sm)',
                  marginBottom: 'var(--silia-space-sm)',
                }}
              />
              <Flex vertical gap={32} style={{ width: '100%' }}>
                <LabelValue labelWidth={40} valueWidth={120} />
                <Flex gap={71} align="center" wrap="wrap">
                  <LabelValue labelWidth={90} valueWidth={140} />
                  <LabelValue labelWidth={50} valueWidth={44} />
                </Flex>
              </Flex>
            </Card>
            <Card
              className="account-settings-add-card"
              style={{
                flex: actualIsMobile ? '1 1 100%' : '0 0 320px',
                width: actualIsMobile ? '100%' : '320px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Skeleton.Button active style={{ width: 140, height: 32 }} />
            </Card>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  );

  return (
    <Flex
      vertical
      aria-busy="true"
      aria-label={t('settings.loadingAria')}
      style={{ margin: actualIsMobile ? 'var(--silia-space-sm)' : 'var(--silia-space-lg)' }}
    >
      {/* Breadcrumb: the loaded page has one above the card, so the card must not jump. */}
      <div className="app-breadcrumb">
        <Skeleton.Input active size="small" style={{ width: 260, minWidth: 0, height: 22 }} />
      </div>
      <Card
        className="account-settings-container"
        style={{
          background: 'var(--silia-bg-container)',
          borderRadius: '8px',
          boxShadow: 'var(--silia-shadow-sm)',
        }}
      >
        <Flex vertical gap={16} style={{ width: '100%' }}>
          <Skeleton.Input
            active
            style={{ width: 140, minWidth: 0, height: isSmallMobile ? 30 : 38 }}
          />
          <Tabs
            activeKey="usage-payment"
            items={[
              {
                key: 'usage-payment',
                label: t('settings.tabs.usagePayment'),
                children: usagePaymentSkeleton,
              },
              { key: 'invoices', label: t('settings.tabs.invoices') },
            ]}
            className="account-settings-tabs"
          />
        </Flex>
      </Card>
    </Flex>
  );
};

/**
 * Loading placeholder for the invoices table's rows.
 *
 * The Table was handed `loading`, which draws AntD's centred Spin over the body:
 * the rows vanish, the wrapper collapses to the spinner's height and the footer
 * jumps when the data lands. These rows hold the block still and mirror the ten
 * columns — plain values for the IDs and amounts, a tag for Agent Type and
 * Status, and the action menu — at the widths the real columns declare.
 *
 * Follows InvoicesTableSkeleton's approach: the block is replaced rather than a
 * `<tbody>` injected through the Table's `components` prop, which would depend on
 * AntD's internal class names.
 */
export const InvoicesRowsSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => {
  /* Widths taken from invoiceColumns; `auto` columns get a nominal one. */
  const cols = [115, 96, 160, 140, 80, 80, 80, 88, 177, 160];

  return (
    <Flex vertical>
      {/* Header labels */}
      <Flex
        align="center"
        gap={16}
        style={{
          padding: 'var(--silia-space-md)',
          borderBottom: '1px solid var(--silia-border-subtle)',
        }}
      >
        {cols.map((w, i) => (
          <Skeleton.Input
            key={i}
            active
            size="small"
            style={{ width: Math.min(w, 88), height: 14, minWidth: 0 }}
          />
        ))}
      </Flex>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, r) => (
        <Flex
          key={r}
          align="center"
          gap={16}
          style={{
            padding: 'var(--silia-space-md)',
            borderBottom: '1px solid var(--silia-border)',
          }}
        >
          {cols.map((w, c) => {
            /* Agent Type (3) and Status (8) render a Tag; Action (9) a menu. */
            const isTag = c === 3 || c === 8;
            const isAction = c === 9;
            if (isAction) {
              return <Skeleton.Avatar key={c} active size="small" shape="circle" />;
            }
            return (
              <Skeleton.Button
                key={c}
                active
                size="small"
                style={{
                  width: Math.min(w, 120),
                  height: isTag ? 22 : 16,
                  minWidth: 0,
                  borderRadius: isTag ? 'var(--silia-radius-pill)' : 'var(--silia-radius-xs)',
                }}
              />
            );
          })}
        </Flex>
      ))}
    </Flex>
  );
};
